import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, RouterStateSnapshot } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';

import { EcmDispatcherModule } from '@ecm/dispatcher';
import { Contract, ContractHelper, PortfolioService, UsageNameToCodeEnum } from '@ecm/portfolio';

import { DeepLink, DeepLinkType } from '../business/deeplink/deeplink';
import { Error } from '../business/error';
import { TrackingData } from '../business/tracking/trackingData';
import { DeepLinkService } from '../services/deeplink.service';
import { ErrorPageService } from '../services/error-page.service';
import { LinkService } from '../services/link.service';
import { RouterService } from '../services/router.service';

@Injectable({
    providedIn: 'root',
})
export class DeepLinkGuard {
    private SELECTOR_CONTRACT = '/selectionner-un-contrat';
    private SELECTOR_USAGE = '/selectionner-un-usage';

    private static CLASS_NAME = 'DeepLinkGuard';

    public constructor(
        private readonly routerService: RouterService,
        private readonly portfolioService: PortfolioService,
        private readonly deepLinkService: DeepLinkService,
        private readonly linkService: LinkService,
        private readonly errorPageService: ErrorPageService,
    ) {}

    public canActivate(_activatedRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        EcmDispatcherModule.sendConsoleInfo(
            DeepLinkGuard.CLASS_NAME,
            `User is logged, will treat deeplink :  ${state.url.toString()}`,
        );
        const code = _activatedRoute.paramMap.get('code');
        return this.callDeepLinkService(code, state?.root?.queryParams);
    }

    private callDeepLinkService(code: string, queryParams: Params): Observable<boolean> {
        return this.deepLinkService.getDeepLinkCode(code).pipe(
            map((deepLink) => {
                const { contractId, usage, ...paramsQuery } = queryParams ?? {};
                const params = this.setParams(contractId, usage);
                if (deepLink.linkType === DeepLinkType.NONE) {
                    // GET Link
                    const linkWeb = this.linkService.getValidWebLink(deepLink.link?.references)?.browser;
                    const trackingData = new TrackingData();
                    trackingData.tmsTracking = {
                        trackingArea: 'deep_link',
                        trackingName: 'deep_link',
                        targetUrl: linkWeb,
                        trackedPage: 'accueil_dashboard',
                    };
                    this.linkService.openLink(
                        deepLink.link,
                        trackingData,
                        params,
                        { queryParams: { ...paramsQuery } },
                        true,
                    );
                } else if (deepLink.linkType === DeepLinkType.CONTRACT) {
                    this.handleDeepLinkContract(deepLink, queryParams, params);
                } else if (deepLink.linkType === DeepLinkType.USAGE) {
                    this.handleDeepLinkUsage(deepLink, queryParams, params);
                }
                return true;
            }),
            catchError((error) => {
                const customDeeplinkError = new Error();
                EcmDispatcherModule.sendConsoleInfo(
                    DeepLinkGuard.CLASS_NAME,
                    'User cannot access the deeplink service, will show default error page',
                );
                customDeeplinkError.title = error.error.customerMessage.message;
                customDeeplinkError.description = error.error.customerMessage.subMessage;
                this.errorPageService.showCustomErrorPage(customDeeplinkError);
                return of(error);
            }),
        );
    }

    private handleDeepLinkContract(deepLink: DeepLink, queryParams?: Params, params?: Map<string, string>) {
        const { contractId, ...paramsQuery } = queryParams ?? {};
        const mapDeepLinkFilters = this.computeMapFilters(deepLink);

        const trackingData = new TrackingData();
        trackingData.tmsTracking = {
            trackingArea: 'deep_link',
            trackingName: 'deep_link',
            targetUrl: '',
            trackedPage: 'accueil_dashboard',
        };

        if (contractId) {
            return this.linkService.openLink(
                deepLink.link,
                trackingData,
                params,
                { queryParams: { ...paramsQuery } },
                true,
            );
        }
        this.portfolioService.getRawContracts().subscribe({
            next: (availableContracts) => {
                const filters = { ...mapDeepLinkFilters, contractId };
                const filteredContracts = ContractHelper.applyFilters(availableContracts, filters);
                if (filteredContracts.length === 0) {
                    this.navigateToFallbackLink(deepLink, undefined, { ...paramsQuery });
                } else {
                    this.navigateToSelectorContracts(deepLink, { ...paramsQuery });
                }
            },
            error: () => {
                this.navigateToFallbackLink(deepLink, undefined, { ...paramsQuery });
            },
        });
    }

    private handleDeepLinkUsage(deepLink: DeepLink, queryParams?: Params, params?: Map<string, string>) {
        const { contractId, usage, ...paramsQuery } = queryParams ?? {};
        let usageTypes = deepLink?.usageFilter?.usageTypes || [];

        if (usage) {
            // eslint-disable-next-line no-unsafe-optional-chaining
            usageTypes = [...new Set<UsageNameToCodeEnum>([...usageTypes, usage])];
        }
        const filters = { contractId, usageTypes };
        const trackingData = new TrackingData();
        trackingData.tmsTracking = {
            trackingArea: 'deep_link',
            trackingName: 'deep_link',
            targetUrl: '',
            trackedPage: 'accueil_dashboard',
        };

        this.portfolioService.getContractsHavingUsage(usage).subscribe({
            next: (contractsHavingUsage) => {
                const filteredContracts = this.applyUsageFilters(filters, contractsHavingUsage);
                if (filteredContracts.length === 0) {
                    this.navigateToFallbackLink(deepLink, usageTypes, { ...paramsQuery });
                } else if (filteredContracts.length === 1) {
                    if (!contractId) {
                        params = this.setParams(filteredContracts[0].cid, usage);
                    }
                    this.linkService.openLink(
                        deepLink.link,
                        trackingData,
                        params,
                        { queryParams: { ...paramsQuery } },
                        true,
                    );
                } else {
                    this.navigateToSelectorUsages(this.SELECTOR_USAGE, deepLink, usageTypes, { ...paramsQuery });
                }
            },
        });
    }

    private setParams(
        contractId: string | undefined = undefined,
        usage: string | undefined = undefined,
    ): Map<string, string> | undefined {
        const paramsMap = new Map<string, string>();
        if (contractId) {
            paramsMap.set('{{cid}}', contractId);
        }
        if (usage) {
            paramsMap.set('{{usage}}', usage);
        }
        return paramsMap;
    }

    // À re-factorer plus tard pour utiliser le sélecteur d'usage
    private applyUsageFilters(filters: any, contracts: Contract[]): Contract[] {
        if (!contracts || !filters) {
            return [];
        }
        let filteredListContract: Contract[] = [...contracts];
        if (filters?.contractId !== undefined) {
            filteredListContract = filteredListContract.filter((currentContract) => {
                return filters?.contractId === currentContract.cid;
            });
        }
        if (filters?.usageTypes.length > 0) {
            filteredListContract = filteredListContract.filter((currentContract) => {
                return currentContract.usages.some((usage) => filters?.usageTypes.includes(usage.usageType));
            });
        }
        return filteredListContract;
    }

    private navigateToSelectorContracts(deepLink: DeepLink, paramsQuery?: Params | undefined) {
        const link = this.linkService.setUrlQueryParams(paramsQuery, this.linkService.getLinkSync(deepLink.link));
        const mapDeepLinkFilters = this.computeMapFilters(deepLink);
        const extras = {
            returnUrl: encodeURI(link),
            ...mapDeepLinkFilters,
        };
        this.routerService.navigate([this.SELECTOR_CONTRACT], {
            queryParams: extras,
        });
    }

    private computeMapFilters(deepLink: DeepLink): any {
        const mapDeepLinkFilters = {};
        if (!deepLink.contractFilter) return mapDeepLinkFilters;
        const KEYS_FILTERS = {
            brand: 'brand',
            marketType: 'marketTypes',
            status: 'state',
            universe: 'types',
        };
        for (const key in KEYS_FILTERS) {
            const contractFilterKey = KEYS_FILTERS[key];
            if (deepLink.contractFilter[contractFilterKey] === undefined) continue;
            mapDeepLinkFilters[key] = deepLink.contractFilter[contractFilterKey];
        }
        return mapDeepLinkFilters;
    }

    private redirect(url: string, paramsQuery?: Params) {
        const urlQueryParams = this.linkService.setUrlQueryParams(paramsQuery, url);
        this.routerService.redirectToExternalUrl(urlQueryParams);
    }

    private navigateToSelectorUsages(
        url: string,
        deepLink: DeepLink,
        usageTypes: UsageNameToCodeEnum | UsageNameToCodeEnum[] | undefined,
        paramsQuery?: Params,
    ) {
        const extras = {
            queryParams: {
                returnUrl: this.linkService.setUrlQueryParams(
                    paramsQuery,
                    this.linkService.getValidWebLink(deepLink.link.references)?.browser,
                ),
                authorizedUsage: usageTypes,
            },
        };
        this.routerService.navigate([url], extras);
    }

    private navigateToFallbackLink(deepLink: DeepLink, usageTypes: any | undefined, paramsQuery?: Params) {
        if (deepLink?.fallbackLink) {
            const urlFallback = this.linkService.getValidWebLink(deepLink.fallbackLink.references)?.browser;
            this.redirect(urlFallback, paramsQuery);
            return;
        }
        if (usageTypes) {
            this.navigateToSelectorUsages(this.SELECTOR_USAGE, deepLink, usageTypes, paramsQuery);
            return;
        }
        this.navigateToSelectorContracts(deepLink, paramsQuery);
    }
}
